import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Play } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface TourStep {
  title: string;
  description: string;
  /** CSS selector of the element to spotlight. Omit for a centred modal. */
  target?: string;
  /** Route to navigate to before showing this step */
  route?: string;
  icon?: string;
  /** Which side of the target to place the tooltip */
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface Rect { x: number; y: number; w: number; h: number }

// ─────────────────────────────────────────────────────────────────────────────
// Per-persona tour definitions
// ─────────────────────────────────────────────────────────────────────────────
const getSystemAdminTour = (userName: string): TourStep[] => [
  {
    title: `Welcome, ${userName}`,
    description: 'You have full control of EIS-Tech. This tour will walk you through your key areas. You can dismiss this at any time and restart it from the Tour button.',
    icon: '🛡️',
    position: 'center'
  },
  {
    title: 'Admin Dashboard',
    description: 'Your command centre — see total companies, users, active frameworks, and compliance health at a glance. Real-time stats update automatically.',
    route: '/admin/dashboard',
    target: 'a[href="/admin/dashboard"]',
    icon: '📊',
    position: 'right'
  },
  {
    title: 'User Management',
    description: 'Create, edit, deactivate, and manage any user across all companies. You can see which company each user belongs to and their permission profile.',
    route: '/admin/users',
    target: 'a[href="/admin/users"]',
    icon: '👥',
    position: 'right'
  },
  {
    title: 'Company Management',
    description: 'Manage all client companies. Each company card shows its admin, industry, size, and compliance status.',
    route: '/admin/companies',
    target: 'a[href="/admin/companies"]',
    icon: '🏢',
    position: 'right'
  },
  {
    title: 'Compliance Frameworks',
    description: 'Define and manage compliance frameworks (ISO 27001, GDPR, etc.). Assign frameworks to companies and track their journey.',
    route: '/compliance/definition/frameworks',
    target: 'a[href="/compliance/definition/frameworks"]',
    icon: '📋',
    position: 'right'
  },
  {
    title: 'Messages & Feedback',
    description: 'Communicate with company admins and auditors. View and respond to feedback submitted by all users.',
    route: '/admin/messages',
    target: 'a[href="/admin/messages"]',
    icon: '💬',
    position: 'right'
  },
  {
    title: 'Audit Logs',
    description: 'Full tamper-evident audit trail of every action in the system. Filter by user, action type, date range, and resource.',
    route: '/admin/audit-logs',
    target: 'a[href="/admin/audit-logs"]',
    icon: '🔍',
    position: 'right'
  },
  {
    title: 'System Settings',
    description: 'Configure AI assistant provider (OpenAI, Claude), model, and API keys. The AI can help users navigate compliance tasks.',
    route: '/admin/system-settings',
    target: 'a[href="/admin/system-settings"]',
    icon: '⚙️',
    position: 'right'
  },
  {
    title: 'Security Settings',
    description: 'Set your personal lockout policy, session timeout, 2FA, and password rotation. Each user can tailor their own security preferences.',
    route: '/admin/settings?tab=security',
    icon: '🔒',
    position: 'center'
  },
  {
    title: "You're all set!",
    description: 'You can restart this tour any time from the Tour button in the header. Explore the left sidebar to access all features.',
    icon: '🎉',
    position: 'center'
  }
];

const getCompanyAdminTour = (userName: string): TourStep[] => [
  {
    title: `Welcome, ${userName}`,
    description: "You manage your organisation's compliance journey. Let's explore what's available to you.",
    icon: '🏢',
    position: 'center'
  },
  {
    title: 'Company Dashboard',
    description: 'Your overview: active frameworks, evidence status, upcoming deadlines, and compliance score — all from live data.',
    route: '/company/dashboard',
    target: 'a[href="/company/dashboard"]',
    icon: '📊',
    position: 'right'
  },
  {
    title: 'Your Users',
    description: 'Manage everyone in your organisation. Add new users, assign permission profiles, and monitor their activity.',
    route: '/company/users',
    target: 'a[href="/company/users"]',
    icon: '👥',
    position: 'right'
  },
  {
    title: 'Compliance Tasks',
    description: 'All tasks assigned to your team. Assign, track, and close tasks. Filter by framework, status, or assignee.',
    route: '/compliance/tasks',
    target: 'a[href="/compliance/tasks"]',
    icon: '✅',
    position: 'right'
  },
  {
    title: 'Evidence Library',
    description: 'Upload and manage evidence documents. Track which evidence is expiring soon and needs renewal.',
    route: '/company/evidence',
    target: 'a[href="/company/evidence"]',
    icon: '📁',
    position: 'right'
  },
  {
    title: 'Monitoring Dashboard',
    description: 'Real-time monitoring of your compliance posture with risk indicators and trend graphs.',
    route: '/compliance/monitoring',
    target: 'a[href="/compliance/monitoring"]',
    icon: '📈',
    position: 'right'
  },
  {
    title: 'Messages',
    description: 'Message your team members, communicate with auditors, and send feedback to the system admin.',
    route: '/company/messages',
    target: 'a[href="/company/messages"]',
    icon: '💬',
    position: 'right'
  },
  {
    title: 'Settings & Security',
    description: 'Configure your account, 2FA, and lockout policy. You can also update your company information here.',
    route: '/company/settings',
    icon: '⚙️',
    position: 'center'
  },
  {
    title: "You're ready!",
    description: 'Restart this tour anytime from the Tour button. Your compliance journey begins now.',
    icon: '🎉',
    position: 'center'
  }
];

const getComplianceUserTour = (userName: string): TourStep[] => [
  {
    title: `Welcome, ${userName}`,
    description: "You're a compliance team member. Here's a quick tour of what you can do.",
    icon: '👋',
    position: 'center'
  },
  {
    title: 'Compliance Dashboard',
    description: 'See your assigned frameworks, pending tasks, and compliance progress. Your starting point every day.',
    route: '/compliance/dashboard',
    target: 'a[href="/compliance/dashboard"]',
    icon: '📊',
    position: 'right'
  },
  {
    title: 'Your Tasks',
    description: 'All tasks assigned to you. Complete them step by step, upload evidence, and submit for review.',
    route: '/compliance/tasks',
    target: 'a[href="/compliance/tasks"]',
    icon: '✅',
    position: 'right'
  },
  {
    title: 'Evidence Upload',
    description: 'Upload supporting documents for controls. Supported formats: PDF, Word, Excel, images.',
    route: '/compliance/uploads',
    target: 'a[href="/compliance/uploads"]',
    icon: '📎',
    position: 'right'
  },
  {
    title: 'Send Feedback',
    description: 'Have a question or found an issue? Send feedback directly to your system admin.',
    route: '/compliance/messages',
    target: 'a[href="/compliance/messages"]',
    icon: '💬',
    position: 'right'
  },
  {
    title: 'All done!',
    description: 'You can restart this tour from the Tour button at any time.',
    icon: '🎉',
    position: 'center'
  }
];

const getInternalAuditorTour = (userName: string): TourStep[] => [
  {
    title: `Welcome, ${userName}`,
    description: "As an internal auditor, you help ensure your company's compliance. This tour will show you your key audit tools and responsibilities.",
    icon: '🔍',
    position: 'center'
  },
  {
    title: 'Auditor Dashboard',
    description: 'Monitor audit progress, review assigned frameworks, and track outstanding assessments.',
    route: '/audit/dashboard',
    target: 'a[href="/audit/dashboard"]',
    icon: '📊',
    position: 'right'
  },
  {
    title: 'Framework Reviews',
    description: 'Review and assess compliance frameworks assigned to your company.',
    route: '/audit/frameworks',
    target: 'a[href="/audit/frameworks"]',
    icon: '📋',
    position: 'right'
  },
  {
    title: 'Evidence Review',
    description: 'Review evidence submitted for compliance. You can approve, request changes, or raise findings.',
    route: '/audit/evidence',
    target: 'a[href="/audit/evidence"]',
    icon: '📎',
    position: 'right'
  },
  {
    title: 'Assessment Summary',
    description: 'View comprehensive summaries of your audit assessments and findings.',
    route: '/audit/summary',
    target: 'a[href="/audit/summary"]',
    icon: '📜',
    position: 'right'
  },
  {
    title: 'All set!',
    description: 'You can restart this tour from the Tour button. Your audit work helps maintain compliance standards.',
    icon: '🎉',
    position: 'center'
  }
];

const getExternalAuditorTour = (userName: string): TourStep[] => [
  {
    title: `Welcome, ${userName}`,
    description: "You have independent access to review compliance evidence and certify frameworks. Your identity is kept separate from company users.",
    icon: '🔍',
    position: 'center'
  },
  {
    title: 'Auditor Dashboard',
    description: 'Your central hub: companies assigned for audit, frameworks to review, and outstanding findings.',
    route: '/audit/dashboard',
    target: 'a[href="/audit/dashboard"]',
    icon: '📊',
    position: 'right'
  },
  {
    title: 'External Audit Queue',
    description: 'Pages submitted for your review. You can certify pages, request changes, or raise findings — all with blind-audit protection.',
    route: '/audit/external',
    target: 'a[href="/audit/external"]',
    icon: '📋',
    position: 'right'
  },
  {
    title: 'Audit Summary',
    description: 'Full summary of your audit actions, findings, and assessments for compliance and defensibility.',
    route: '/audit/summary',
    target: 'a[href="/audit/summary"]',
    icon: '📜',
    position: 'right'
  },
  {
    title: 'Certifications',
    description: 'Issue and manage certifications once all evidence has been reviewed and approved.',
    route: '/certification/issue',
    target: 'a[href="/certification/issue"]',
    icon: '🏆',
    position: 'right'
  },
  {
    title: 'Messages',
    description: 'Communicate with the system admin if you need access or have concerns.',
    route: '/audit/messages',
    target: 'a[href="/audit/messages"]',
    icon: '💬',
    position: 'right'
  },
  {
    title: "You're set!",
    description: 'Restart this tour from the Tour button. Your independence and objectivity are protected throughout.',
    icon: '🎉',
    position: 'center'
  }
];

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const TOUR_SEEN_KEY = 'eis_tour_seen';
const TOUR_TRIGGER_KEY = 'eis_tour_start';
const TOOLTIP_W = 360;
const TOOLTIP_H_APPROX = 220; // rough estimate for positioning
const PAD = 16; // padding around spotlight cutout

// ─────────────────────────────────────────────────────────────────────────────
// Hook: find target element with retry
// ─────────────────────────────────────────────────────────────────────────────
function useTargetRect(
  selector: string | undefined,
  active: boolean,
  stepIndex: number
): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!active || !selector) {
      setRect(null);
      return;
    }

    let rafId: number;
    let attempts = 0;
    const maxAttempts = 30; // up to ~900ms

    const tryFind = () => {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
          return;
        }
      }
      attempts++;
      if (attempts < maxAttempts) {
        rafId = requestAnimationFrame(tryFind);
      } else {
        setRect(null); // not found → fall back to centre
      }
    };

    // Short delay to allow route transition to complete
    const timeout = setTimeout(() => { rafId = requestAnimationFrame(tryFind); }, 300);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(rafId);
    };
  }, [active, selector, stepIndex]);

  // Update on scroll / resize
  useEffect(() => {
    if (!active || !selector || !rect) return;
    const update = () => {
      const el = document.querySelector(selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      }
    };
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [active, selector, rect]);

  return rect;
}

// ─────────────────────────────────────────────────────────────────────────────
// SVG Spotlight Overlay
// ─────────────────────────────────────────────────────────────────────────────
const SpotlightOverlay: React.FC<{ targetRect: Rect | null }> = ({ targetRect }) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (!targetRect) {
    // No target: gentle dark vignette around the centre, not a full blackout
    return (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(15,23,42,0.55)',
          pointerEvents: 'none',
        }}
      />
    );
  }

  const { x, y, w, h } = targetRect;
  const cx = x - PAD;
  const cy = y - PAD;
  const cw = w + PAD * 2;
  const ch = h + PAD * 2;
  const r = 10; // corner radius

  // SVG path: full-screen rect minus a rounded-rect hole
  const path = [
    `M 0 0 H ${vw} V ${vh} H 0 Z`,
    `M ${cx + r} ${cy}`,
    `H ${cx + cw - r} Q ${cx + cw} ${cy} ${cx + cw} ${cy + r}`,
    `V ${cy + ch - r} Q ${cx + cw} ${cy + ch} ${cx + cw - r} ${cy + ch}`,
    `H ${cx + r} Q ${cx} ${cy + ch} ${cx} ${cy + ch - r}`,
    `V ${cy + r} Q ${cx} ${cy} ${cx + r} ${cy} Z`,
  ].join(' ');

  return (
    <svg
      style={{
        position: 'fixed', inset: 0, width: '100%', height: '100%',
        zIndex: 10000, pointerEvents: 'none',
        transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
      }}
    >
      {/* Glowing ring around the spotlight */}
      <rect
        x={cx - 2} y={cy - 2} width={cw + 4} height={ch + 4} rx={r + 2}
        fill="none" stroke="#3b82f6" strokeWidth="2.5"
        style={{ filter: 'drop-shadow(0 0 8px #3b82f6aa)' }}
      />
      {/* Dark overlay with hole */}
      <path d={path} fill="rgba(15,23,42,0.65)" fillRule="evenodd" />
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip card with smart positioning
// ─────────────────────────────────────────────────────────────────────────────
interface TooltipProps {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  targetRect: Rect | null;
  onNext: () => void;
  onPrev: () => void;
  onDismiss: () => void;
}

const TooltipCard: React.FC<TooltipProps> = ({
  step, stepIndex, totalSteps, targetRect, onNext, onPrev, onDismiss
}) => {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const progress = ((stepIndex + 1) / totalSteps) * 100;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const MARGIN = 12;

  let cardStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10001,
    width: TOOLTIP_W,
    maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
  };

  let arrowStyle: React.CSSProperties | null = null;

  if (!targetRect || step.position === 'center') {
    // Centre of screen
    cardStyle = {
      ...cardStyle,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  } else {
    const { x, y, w, h } = targetRect;
    const spotLeft = x - PAD;
    const spotTop = y - PAD;
    const spotRight = x + w + PAD;
    const spotBottom = y + h + PAD;
    const cx = x + w / 2; // center x of target
    const cy = y + h / 2; // center y of target

    // Determine best side based on available space
    const spaceRight = vw - spotRight;
    const spaceLeft = spotLeft;
    const spaceBottom = vh - spotBottom;
    const spaceTop = spotTop;

    let side = step.position ?? 'right';

    // Auto-correct if preferred side has insufficient room
    if (side === 'right' && spaceRight < TOOLTIP_W + MARGIN * 2) {
      side = spaceLeft > TOOLTIP_W + MARGIN * 2 ? 'left' : spaceBottom > TOOLTIP_H_APPROX + MARGIN * 2 ? 'bottom' : 'top';
    } else if (side === 'left' && spaceLeft < TOOLTIP_W + MARGIN * 2) {
      side = spaceRight > TOOLTIP_W + MARGIN * 2 ? 'right' : spaceBottom > TOOLTIP_H_APPROX + MARGIN * 2 ? 'bottom' : 'top';
    } else if (side === 'bottom' && spaceBottom < TOOLTIP_H_APPROX + MARGIN * 2) {
      side = spaceTop > TOOLTIP_H_APPROX + MARGIN * 2 ? 'top' : spaceRight > TOOLTIP_W + MARGIN * 2 ? 'right' : 'left';
    } else if (side === 'top' && spaceTop < TOOLTIP_H_APPROX + MARGIN * 2) {
      side = spaceBottom > TOOLTIP_H_APPROX + MARGIN * 2 ? 'bottom' : spaceRight > TOOLTIP_W + MARGIN * 2 ? 'right' : 'left';
    }

    const arrowBase: React.CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      border: '8px solid transparent',
    };

    if (side === 'right') {
      const top = Math.max(MARGIN, Math.min(cy - TOOLTIP_H_APPROX / 2, vh - TOOLTIP_H_APPROX - MARGIN));
      cardStyle = { ...cardStyle, left: spotRight + MARGIN, top };
      arrowStyle = {
        ...arrowBase,
        left: -16,
        top: Math.min(cy - top - 8, TOOLTIP_H_APPROX - 24),
        borderRightColor: '#fff',
        borderLeftWidth: 0,
      };
    } else if (side === 'left') {
      const top = Math.max(MARGIN, Math.min(cy - TOOLTIP_H_APPROX / 2, vh - TOOLTIP_H_APPROX - MARGIN));
      cardStyle = { ...cardStyle, right: vw - spotLeft + MARGIN, top };
      arrowStyle = {
        ...arrowBase,
        right: -16,
        top: Math.min(cy - top - 8, TOOLTIP_H_APPROX - 24),
        borderLeftColor: '#fff',
        borderRightWidth: 0,
      };
    } else if (side === 'bottom') {
      const left = Math.max(MARGIN, Math.min(cx - TOOLTIP_W / 2, vw - TOOLTIP_W - MARGIN));
      cardStyle = { ...cardStyle, top: spotBottom + MARGIN, left };
      arrowStyle = {
        ...arrowBase,
        top: -16,
        left: Math.min(cx - left - 8, TOOLTIP_W - 24),
        borderBottomColor: '#fff',
        borderTopWidth: 0,
      };
    } else { // top
      const left = Math.max(MARGIN, Math.min(cx - TOOLTIP_W / 2, vw - TOOLTIP_W - MARGIN));
      cardStyle = { ...cardStyle, bottom: vh - spotTop + MARGIN, left };
      arrowStyle = {
        ...arrowBase,
        bottom: -16,
        left: Math.min(cx - left - 8, TOOLTIP_W - 24),
        borderTopColor: '#fff',
        borderBottomWidth: 0,
      };
    }
  }

  return (
    <div style={cardStyle}>
      {/* Arrow pointing to target */}
      {arrowStyle && (
        <div style={{ ...arrowStyle, position: 'absolute' }} />
      )}

      <div
        className="bg-white rounded-2xl overflow-hidden"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.08)' }}
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{step.icon || '📌'}</span>
              <div>
                <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest mb-0.5">
                  Step {stepIndex + 1} of {totalSteps}
                </p>
                <h3 className="text-base font-bold text-white leading-tight">{step.title}</h3>
              </div>
            </div>
            <button
              onClick={onDismiss}
              className="text-blue-300 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 shrink-0 mt-0.5"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
        </div>

        {/* Footer */}
        <div className="px-5 pb-4 flex items-center justify-between">
          {/* Dot indicators */}
          <div className="flex gap-1 flex-wrap">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === stepIndex
                    ? 'w-5 bg-blue-600'
                    : i < stepIndex
                    ? 'w-1.5 bg-blue-300'
                    : 'w-1.5 bg-slate-200'
                }`}
              />
            ))}
          </div>
          {/* Navigation buttons */}
          <div className="flex gap-2 shrink-0">
            {!isFirst && (
              <button
                onClick={onPrev}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <ChevronLeft size={15} /> Back
              </button>
            )}
            <button
              onClick={onNext}
              className="flex items-center gap-1 px-4 py-1.5 text-sm font-bold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow"
            >
              {isLast ? 'Finish' : 'Next'} {!isLast && <ChevronRight size={15} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Tour component
// ─────────────────────────────────────────────────────────────────────────────
const OnboardingTour: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const steps: TourStep[] = React.useMemo((): TourStep[] => {
    if (!user) return [];

    const userName = user.first_name || 'User';

    if (hasPermission('system.config')) return getSystemAdminTour(userName);
    if (hasPermission('companies.manage')) return getCompanyAdminTour(userName);
    if (hasPermission('audit.dashboard.view')) {
      // Distinguish between internal and external auditors
      if (user.company_id) {
        return getInternalAuditorTour(userName);
      } else {
        return getExternalAuditorTour(userName);
      }
    }
    return getComplianceUserTour(userName);
  }, [user, hasPermission]);

  const currentStep = steps[stepIndex];
  const targetRect = useTargetRect(currentStep?.target, active, stepIndex);

  // Auto-start for first-time users
  useEffect(() => {
    if (!user) return undefined;
    const seen = localStorage.getItem(`${TOUR_SEEN_KEY}:${user.id}`);
    if (!seen) {
      const t = setTimeout(() => setActive(true), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [user]);

  // Listen for manual trigger
  useEffect(() => {
    const handler = () => { setStepIndex(0); setActive(true); };
    window.addEventListener(TOUR_TRIGGER_KEY, handler);
    return () => window.removeEventListener(TOUR_TRIGGER_KEY, handler);
  }, []);

  // Navigate to step route
  useEffect(() => {
    if (!active || !currentStep?.route) return;
    const targetPath = currentStep.route.split('?')[0];
    const currentPath = location.pathname;
    if (currentPath !== targetPath) {
      navigate(currentStep.route, { replace: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIndex]);

  const dismiss = useCallback(() => {
    setActive(false);
    if (user) localStorage.setItem(`${TOUR_SEEN_KEY}:${user.id}`, '1');
  }, [user]);

  const next = useCallback(() => {
    if (stepIndex >= steps.length - 1) dismiss();
    else setStepIndex(i => i + 1);
  }, [stepIndex, steps.length, dismiss]);

  const prev = useCallback(() => setStepIndex(i => Math.max(0, i - 1)), []);

  if (!active || !user || steps.length === 0 || !currentStep) return null;

  return createPortal(
    <>
      <SpotlightOverlay targetRect={targetRect} />
      <TooltipCard
        step={currentStep}
        stepIndex={stepIndex}
        totalSteps={steps.length}
        targetRect={targetRect}
        onNext={next}
        onPrev={prev}
        onDismiss={dismiss}
      />
    </>,
    document.body
  );
};

export default OnboardingTour;

// ─────────────────────────────────────────────────────────────────────────────
// Helper: start tour programmatically
// ─────────────────────────────────────────────────────────────────────────────
export function startTour() {
  window.dispatchEvent(new Event(TOUR_TRIGGER_KEY));
}

// ─────────────────────────────────────────────────────────────────────────────
// TourHelpButton — add to the header; clicking restarts the tour
// ─────────────────────────────────────────────────────────────────────────────
export const TourHelpButton: React.FC = () => (
  <button
    onClick={startTour}
    title="Start guided tour"
    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1"
  >
    <Play size={16} />
    <span className="hidden sm:inline text-xs font-bold">Tour</span>
  </button>
);
